<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import { push, link } from "svelte-spa-router";
  import {
    listDevices,
    registerDevice,
    revokeDevice,
    rebootDevice,
    updateDevice,
    changeDeviceBranch,
    changeDevicePin,
    setDeviceRoom,
    listRooms,
    renameDevice,
    setDeviceDisabled,
    updateAllDevices,
    ApiError,
    type ReleaseChannel,
  } from "../lib/api";
  import Breadcrumb from "../lib/Breadcrumb.svelte";
  import { session, scopes } from "../lib/session.svelte";
  import type { AdminRoom } from "@diy-avatar/shared/admin";
  import type {
    DeviceListEntry as BaseDeviceListEntry,
    ChangeBranchRequest,
  } from "@diy-avatar/shared/admin";

  /**
   * Release fields the backend now adds to each device list item. Kept local
   * until the shared type is published; merged onto the base entry.
   */
  interface DeviceReleaseInfo {
    installed_tag?: string | null;
    installed_commit?: string | null;
    release_time?: string | null;
    channel?: ReleaseChannel | null;
    update?: {
      available: boolean;
      target_tag: string;
      releases_behind: number;
      commits_behind: number;
    } | null;
  }
  type DeviceListEntry = BaseDeviceListEntry & DeviceReleaseInfo;

  let devices: DeviceListEntry[] = [];
  let rooms: AdminRoom[] = [];
  let generatedAt: number | null = null;
  let error: string | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;

  // Register form
  let newLabel = "";
  let registering = false;
  let newToken: string | null = null;
  let newTokenLabel: string | null = null;

  let updatingAll: string | null = null;

  async function refresh() {
    try {
      const res = await listDevices();
      devices = res.devices as DeviceListEntry[];
      generatedAt = res.generated_at;
      error = null;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
      error = err instanceof Error ? err.message : "Could not load devices.";
    }
  }

  async function loadRooms() {
    try {
      rooms = (await listRooms()).rooms;
    } catch {
      rooms = [];
    }
  }

  async function doSetRoom(d: DeviceListEntry, roomId: number | null) {
    error = null;
    try {
      await setDeviceRoom(d.label, roomId);
      await refresh();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
      error = err instanceof Error ? err.message : "set room failed";
    }
  }

  // Only an admin may register/revoke raw devices.
  $: isAdmin = $scopes.isAdmin;

  async function doRegister() {
    error = null;
    if (!newLabel.trim()) {
      error = "label required";
      return;
    }
    registering = true;
    try {
      const res = await registerDevice(newLabel.trim());
      newToken = res.token;
      newTokenLabel = res.label;
      newLabel = "";
      await refresh();
    } catch (err) {
      error =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "register failed";
    } finally {
      registering = false;
    }
  }

  async function act(
    fn: () => Promise<unknown>,
    label: string,
    confirmMsg?: string,
  ) {
    if (confirmMsg && !confirm(confirmMsg)) return;
    error = null;
    try {
      await fn();
      await refresh();
    } catch (err) {
      error =
        err instanceof Error ? err.message : `action failed for ${label}`;
    }
  }

  function doReboot(d: DeviceListEntry) {
    void act(() => rebootDevice(d.label), d.label, `Reboot ${d.label}?`);
  }
  function doUpdate(d: DeviceListEntry) {
    void act(() => updateDevice(d.label), d.label, `Update ${d.label}?`);
  }
  function doRevoke(d: DeviceListEntry) {
    void act(() => revokeDevice(d.label), d.label, `Revoke ${d.label}? This deletes the device.`);
  }
  function doChannel(d: DeviceListEntry, channel: ChangeBranchRequest["channel"]) {
    void act(() => changeDeviceBranch(d.label, channel), d.label);
  }
  function doPin(d: DeviceListEntry) {
    const pin = prompt(`New PIN for ${d.label} (4-8 digits)?`);
    if (!pin) return;
    void act(() => changeDevicePin(d.label, pin), d.label);
  }
  function doRename(d: DeviceListEntry) {
    const next = prompt(`Rename ${d.label} to:`, d.label);
    if (!next) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === d.label) return;
    void act(() => renameDevice(d.label, trimmed), d.label);
  }
  function doToggleDisabled(d: DeviceListEntry) {
    const next = !d.disabled;
    const verb = next ? "Disable" : "Enable";
    void act(
      () => setDeviceDisabled(d.label, next),
      d.label,
      `${verb} ${d.label}?`,
    );
  }

  async function doUpdateAll(channel: ReleaseChannel) {
    if (!confirm(`Update ALL devices on ${channel}?`)) return;
    error = null;
    updatingAll = channel;
    try {
      const res = await updateAllDevices(channel);
      await refresh();
      error = null;
      alert(`Queued ${res.queued} update(s) on ${channel}.`);
    } catch (err) {
      error = err instanceof Error ? err.message : "update-all failed";
    } finally {
      updatingAll = null;
    }
  }

  // Distinct channels present across the device list, for "Update all" controls.
  $: channels = Array.from(
    new Set(
      devices
        .map((d) => d.channel)
        .filter((c): c is ReleaseChannel => c === "LIVE" || c === "BETA"),
    ),
  );

  function shortHash(h: string | null | undefined): string {
    return h ? h.slice(0, 7) : "";
  }
  function dateOnly(s: string | null | undefined): string {
    if (!s) return "";
    return s.slice(0, 10);
  }
  function dateTime(s: string | null | undefined): string {
    if (!s) return "";
    const d = new Date(s);
    return isNaN(d.getTime()) ? s : d.toLocaleString();
  }
  function installedText(d: DeviceListEntry): string {
    const parts = [
      d.installed_tag,
      shortHash(d.installed_commit),
      dateTime(d.release_time),
    ].filter((p) => !!p);
    return parts.length ? parts.join(" · ") : "—";
  }
  function updateText(d: DeviceListEntry): string {
    const u = d.update;
    if (!u || !u.available) return "up to date";
    const from = d.installed_tag ?? "?";
    return `${from} → ${u.target_tag} (${u.releases_behind} releases, ${u.commits_behind} commits behind)`;
  }
  function versionText(d: DeviceListEntry): string {
    const g = d.system?.git;
    if (!g) return "—";
    const parts = [
      g.tag,
      shortHash(g.hash),
      dateOnly(g.commit_date),
    ].filter((p) => !!p);
    return parts.length ? parts.join(" · ") : "—";
  }
  function osText(d: DeviceListEntry): string {
    const os = d.system?.os;
    if (!os) return "—";
    const parts = [os.pretty_name, os.kernel].filter((p) => !!p);
    let txt = parts.length ? parts.join(" · ") : "—";
    if (os.last_update_ts) {
      txt += ` (upd ${new Date(os.last_update_ts).toISOString().slice(0, 10)})`;
    }
    return txt;
  }
  function lastOnlineText(d: DeviceListEntry): string {
    if (!d.last_heartbeat_ts) return "never";
    if (d.last_heartbeat_age_s != null) return `${d.last_heartbeat_age_s}s ago`;
    return new Date(d.last_heartbeat_ts).toISOString();
  }
  function studentsText(d: DeviceListEntry): string {
    return d.students_connected.length ? d.students_connected.join(", ") : "—";
  }
  function encState(d: DeviceListEntry): "encrypted" | "force-pending" | "off" {
    if (d.e2ee_key_id) return "encrypted";
    if (d.force_encryption) return "force-pending";
    return "off";
  }
  function encIcon(d: DeviceListEntry): string {
    const s = encState(d);
    return s === "encrypted" ? "🔒" : "🔓";
  }
  function encTitle(d: DeviceListEntry): string {
    switch (encState(d)) {
      case "encrypted":
        return `Encrypted · key ${d.e2ee_key_id}`;
      case "force-pending":
        return "Force-encryption enabled (no active key)";
      default:
        return "Unencrypted";
    }
  }

  function copyToken() {
    if (newToken && navigator.clipboard) {
      void navigator.clipboard.writeText(newToken);
    }
  }

  onMount(() => {
    if (!$session.user) {
      push("/");
      return;
    }
    void refresh();
    void loadRooms();
    timer = setInterval(() => void refresh(), 5000);
  });

  onDestroy(() => {
    if (timer) clearInterval(timer);
  });
</script>

<section>
  <Breadcrumb items={[{ label: "Devices" }]} />
  <h1>Devices</h1>
  {#if error}
    <p class="error" data-testid="error">{error}</p>
  {/if}

  {#if isAdmin}
  <div class="new" data-testid="register-device">
    <h2>Register device</h2>
    <div class="row">
      <input
        type="text"
        placeholder="label (e.g. classroom-1)"
        bind:value={newLabel}
        data-testid="register-label"
      />
      <button on:click={doRegister} disabled={registering} data-testid="register-submit">
        {registering ? "Registering…" : "Register"}
      </button>
    </div>
    {#if newToken}
      <div class="token" data-testid="register-token">
        <p class="token-note">
          Token for <strong>{newTokenLabel}</strong> — copy now, shown once:
        </p>
        <div class="token-row">
          <input type="text" readonly value={newToken} data-testid="register-token-value" />
          <button type="button" on:click={copyToken} data-testid="register-token-copy">
            Copy
          </button>
          <button
            type="button"
            on:click={() => {
              newToken = null;
              newTokenLabel = null;
            }}
            data-testid="register-token-dismiss"
          >
            Dismiss
          </button>
        </div>
      </div>
    {/if}
  </div>

  {/if}

  {#if isAdmin}
  <div class="bulk" data-testid="update-all">
    <span class="bulk-label">Update all on channel:</span>
    {#if channels.length}
      {#each channels as ch (ch)}
        <button
          on:click={() => doUpdateAll(ch)}
          disabled={updatingAll !== null}
          data-testid="update-all-{ch}"
        >
          {updatingAll === ch ? "Queuing…" : `Update all on ${ch}`}
        </button>
      {/each}
    {:else}
      <button
        on:click={() => doUpdateAll("LIVE")}
        disabled={updatingAll !== null}
        data-testid="update-all-LIVE"
      >
        {updatingAll === "LIVE" ? "Queuing…" : "Update all on LIVE"}
      </button>
      <button
        on:click={() => doUpdateAll("BETA")}
        disabled={updatingAll !== null}
        data-testid="update-all-BETA"
      >
        {updatingAll === "BETA" ? "Queuing…" : "Update all on BETA"}
      </button>
    {/if}
  </div>
  {/if}

  <table data-testid="devices-table">
    <thead>
      <tr>
        <th>id</th>
        <th>Status</th>
        <th>Enabled</th>
        <th>Label</th>
        <th>Room</th>
        <th>Last online</th>
        <th>Version</th>
        <th>Installed</th>
        <th>Channel</th>
        <th>Update</th>
        <th>OS</th>
        <th title="Gates the device's local settings overlay — not the kiosk session.">Settings PIN</th>
        <th>Students</th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      {#each devices as d (d.label)}
        <tr data-testid="device-row-{d.label}" class:disabled={d.disabled}>
          <td>{d.label}</td>
          <td>
            <span class="dot" class:online={d.online}></span>
            {d.online ? "online" : "offline"}
            <span
              class="enc-badge enc-{encState(d)}"
              title={encTitle(d)}
              data-testid="device-enc-{d.label}"
            >{encIcon(d)}</span>
            {#if d.force_e2ee_qr}
              <span class="qr-tag" title="In-person QR key exchange forced">QR</span>
            {/if}
          </td>
          <td>
            <button
              class:danger={d.disabled}
              on:click={() => doToggleDisabled(d)}
              title={d.disabled ? "Device is disabled — click to enable" : "Device is enabled — click to disable"}
              aria-label={d.disabled ? `Enable ${d.label}` : `Disable ${d.label}`}
              data-testid="device-disabled-{d.label}"
            >
              {d.disabled ? "Disabled" : "Enabled"}
            </button>
          </td>
          <td>
            <a href="/devices/{encodeURIComponent(d.label)}" use:link data-testid="device-link-{d.label}">
              {d.label}
            </a>
          </td>
          <td>
            <select
              value={d.room_id ?? ""}
              on:change={(e) =>
                doSetRoom(d, e.currentTarget.value === "" ? null : Number(e.currentTarget.value))}
              data-testid="device-room-{d.label}"
            >
              <option value="">— unassigned —</option>
              {#each rooms as r (r.id)}
                <option value={r.id}>{r.name}</option>
              {/each}
            </select>
          </td>
          <td>{lastOnlineText(d)}</td>
          <td>{versionText(d)}</td>
          <td>{installedText(d)}</td>
          <td>
            <select
              value={d.channel ?? "LIVE"}
              on:change={(e) => doChannel(d, e.currentTarget.value === "BETA" ? "BETA" : e.currentTarget.value === "master" ? "master" : e.currentTarget.value === "TESTING" ? "TESTING" : "LIVE")}
              aria-label="channel for {d.label}"
              data-testid="device-channel-{d.label}"
            >
              <option value="LIVE">LIVE</option>
              <option value="BETA">BETA</option>
              <option value="TESTING">TESTING</option>
              <option value="master">master</option>
            </select>
          </td>
          <td>
            {#if d.update?.available}
              <span class="badge update" data-testid="update-badge-{d.label}">update</span>
              <span class="update-text">{updateText(d)}</span>
            {:else}
              <span class="update-ok">up to date</span>
            {/if}
          </td>
          <td>{osText(d)}</td>
          <td title="Gates the device's local settings overlay — not the kiosk session.">{d.settings_pin ?? "—"}</td>
          <td>{studentsText(d)}</td>
          <td class="actions">
            <a href="/devices/{encodeURIComponent(d.label)}" use:link>Open →</a>
            {#if isAdmin}
              <button on:click={() => doReboot(d)}>Reboot</button>
              <button on:click={() => doUpdate(d)}>Update</button>
              <button on:click={() => doRename(d)} aria-label="Rename {d.label}">Rename</button>
              <button on:click={() => doPin(d)} title="Set the device's local settings-overlay PIN">PIN</button>
              <button on:click={() => doRevoke(d)}>Revoke</button>
            {/if}
          </td>
        </tr>
      {/each}
      {#if devices.length === 0}
        <tr><td colspan="14" class="empty">No devices registered.</td></tr>
      {/if}
    </tbody>
  </table>
  <p class="ts" data-testid="generated-at">
    generated_at: {generatedAt ? new Date(generatedAt).toISOString() : "—"}
  </p>
</section>

<style>
  h1 {
    margin: 0 0 1rem;
  }
  h2 {
    margin: 0 0 0.5rem;
    font-size: 1rem;
    color: #34d399;
  }
  .new {
    margin-bottom: 1rem;
    padding: 0.75rem 1rem;
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
  }
  .row {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  input,
  select {
    padding: 0.4rem 0.5rem;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
  }
  button {
    padding: 0.4rem 0.7rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 4px;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    background: #111827;
  }
  th,
  td {
    padding: 0.4rem 0.6rem;
    text-align: left;
    border-bottom: 1px solid #1f2937;
    font-size: 0.85rem;
    vertical-align: middle;
  }
  th {
    background: #0f172a;
    color: #9ca3af;
  }
  td a {
    color: #34d399;
    text-decoration: none;
  }
  td a:hover {
    text-decoration: underline;
  }
  .actions {
    display: flex;
    gap: 0.3rem;
    flex-wrap: wrap;
    align-items: center;
  }
  .dot {
    display: inline-block;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #6b7280;
    margin-right: 0.3rem;
    vertical-align: middle;
  }
  .dot.online {
    background: #10b981;
  }
  tr.disabled td {
    opacity: 0.5;
  }
  tr.disabled td:first-child,
  tr.disabled td:nth-child(2) {
    opacity: 1;
  }
  button.danger {
    background: #7f1d1d;
    border-color: #b91c1c;
    color: #fecaca;
  }
  .empty {
    color: #6b7280;
    text-align: center;
  }
  .token {
    margin-top: 0.75rem;
  }
  .token-note {
    color: #fbbf24;
    font-size: 0.85rem;
    margin: 0 0 0.3rem;
  }
  .token-row {
    display: flex;
    gap: 0.4rem;
  }
  .token-row input {
    flex: 1;
    font-family: ui-monospace, monospace;
  }
  .ts {
    color: #6b7280;
    font-size: 0.8rem;
    margin-top: 0.75rem;
  }
  .error {
    color: #fca5a5;
  }
  .bulk {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-bottom: 1rem;
  }
  .bulk-label {
    color: #9ca3af;
    font-size: 0.85rem;
  }
  .badge {
    display: inline-block;
    padding: 0.1rem 0.4rem;
    border-radius: 4px;
    font-size: 0.7rem;
    font-weight: 700;
  }
  .badge.update {
    background: #3b2f06;
    color: #fbbf24;
  }
  .update-text {
    display: block;
    color: #fbbf24;
    font-size: 0.78rem;
    margin-top: 0.15rem;
  }
  .update-ok {
    color: #6ee7b7;
    font-size: 0.8rem;
  }
  .enc-badge {
    font-size: 0.8rem;
    margin-left: 0.3rem;
    vertical-align: middle;
  }
  .enc-badge.enc-encrypted {
    color: #34d399;
  }
  .enc-badge.enc-force-pending {
    color: #fbbf24;
  }
  .enc-badge.enc-off {
    color: #6b7280;
  }
  .qr-tag {
    display: inline-block;
    margin-left: 0.25rem;
    padding: 0 0.25rem;
    border-radius: 3px;
    background: #1e3a8a;
    color: #bfdbfe;
    font-size: 0.6rem;
    font-weight: 700;
    vertical-align: middle;
  }
</style>
